import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Apartment } from '../types/Apartment';
import ApartmentList from '../components/ApartmentList';
import ApartmentCard from '../components/ApartmentCard';
import ApartmentTableView from '../components/ApartmentTableView';
import PureGlassmorphicFilterPanel from '../components/PureGlassmorphicFilterPanel';
import Modal from '../components/Modal';
import FloorPlanViewer from '../components/FloorPlanViewer';
import { formatPrice } from '../config/priceConfig';
import FullscreenFloorPlanViewer from '../components/FullscreenFloorPlanViewer';
import { useApartments } from '../context/ApartmentContext';
import Viewer360 from '../components/Viewer360';
import { apartments as defaultApartments, buildingImages, buildingRealImages, getApartmentPDFName } from '../data/apartments';
import { convertStandardToRealIndex, convertRealToStandardIndex } from '../firebase/utils';
import { 
  WiSunrise, 
  WiDaySunny, 
  WiNightClear 
} from 'react-icons/wi';
import { Check, Trash2, Building2, BedDouble, ArrowUp, Info, Settings, List, Eye, EyeOff, Filter } from 'lucide-react';
import { useDeviceDetection, mediaQueries } from '../hooks/useDeviceDetection';

// Typeform interface declaration
declare global {
  interface Window {
    tf?: {
      load?: () => void;
    };
  }
}

// Mobile view constants
const MOBILE_VIEW_VIEWER = 'viewer';
const MOBILE_VIEW_LIST = 'list';

// Loading state component
const LoadingCard = styled.div`
  @media ${mediaQueries.mobile} {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
      );
      animation: shimmer 1.5s infinite;
      z-index: 1;
    }
  }
`;

const LoadingSkeleton = styled.div<{ width?: string; height?: string; margin?: string }>`
  background: linear-gradient(135deg, #e5e7eb, #f3f4f6);
  border-radius: 8px;
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '16px'};
  margin: ${props => props.margin || '0 0 12px 0'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    animation: shimmer 1.5s infinite;
  }
  
  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

// Enhanced mobile animations
const AnimatedContainer = styled.div<{ delay?: number }>`
  @media (max-width: 850px) {
    animation: fadeInUp 0.6s ease-out forwards;
    animation-delay: ${props => props.delay || 0}ms;
    opacity: 0;
  }
`;

const Container = styled.div<{ mobileView?: string }>`
  /* Immersive fullscreen layout */
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000000;
  overflow: hidden;
  
  @media (max-width: 850px) {
    height: calc(100vh - 76px);
    top: 76px;
    background: ${props => props.mobileView === 'viewer' ? '#000000' : '#ffffff'};
  }
`;

const Sidebar = styled.div`
  width: 400px;
  background: white;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  padding: 24px;
  z-index: 10;
  border-right: 1px solid #e9ecef;

  @media (max-width: 850px) {
    width: 100%;
    padding: 16px;
    box-shadow: none;
    border-bottom: 1px solid #e0e0e0;
    max-height: 100vh;
    order: 2;
  }
`;

// Elegant glassmorphic sidebar
const ElegantSidebar = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-420px'};
  width: 420px;
  height: 100vh;
  /* True glassmorphic background - minimal opacity for blur-through effect */
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.1), 
    rgba(248, 250, 252, 0.05),
    rgba(241, 245, 249, 0.08)
  );
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  
  /* Add subtle pattern overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: 0 0, 10px 10px;
    pointer-events: none;
    z-index: -1;
  }
  
  @media (max-width: 850px) {
    width: 100vw;
    right: ${props => props.isOpen ? '0' : '-100vw'};
    border-left: none;
    height: calc(100vh - 76px);
    top: 76px;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.15), 
      rgba(248, 250, 252, 0.1),
      rgba(241, 245, 249, 0.12)
    );
  }
`;

const FloatingMenuButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  top: 32px;
  right: 32px;
  background: ${props => props.isOpen 
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
  };
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  z-index: 2001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    border-color: rgba(59, 130, 246, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 850px) {
    top: 100px;
    right: 20px;
    width: 48px;
    height: 48px;
    border-radius: 24px;
    padding: 0;
    gap: 0;
    font-size: 0; /* Hide text on mobile */
  }
`;

const ViewSwitchButton = styled.button`
  position: fixed;
  top: 32px;
  left: 32px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  z-index: 2001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    border-color: rgba(59, 130, 246, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 850px) {
    display: none;
  }
`;

const ListViewButton = styled.button<{ isActive?: boolean }>`
  position: fixed;
  top: 32px;
  left: 220px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(59, 130, 246, 0.6);
  border-radius: 16px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  z-index: 2001;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #2563eb, #1e40af);
    border-color: rgba(59, 130, 246, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 850px) {
    display: none;
  }
`;

const SVGToggleButton = styled.button<{ isVisible?: boolean }>`
  position: fixed;
  top: 32px;
  left: 380px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  z-index: 2001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    border-color: rgba(226, 232, 240, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 850px) {
    display: none;
  }
`;

const ViewerContainer = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;
  filter: ${props => props.isBlurred ? 'blur(8px)' : 'none'};
  transition: filter 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    opacity: ${props => props.isBlurred ? 1 : 0};
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
`;

const MobileViewSwitchButton = styled.button`
  width: 100%;
  padding: 16px 20px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
  
  &:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
  }
  
  &:active {
    transform: translateY(-1px);
    transition: transform 0.1s ease;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
    }
  }
`;


const SidebarContent = styled.div`
  padding: 48px 32px;
  height: 100%;
  overflow-y: auto;
  
  @media (max-width: 850px) {
    padding: 32px 24px;
  }
`;

const SidebarHeader = styled.div`
  margin-bottom: 48px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 32px;
`;

const ElegantTitle = styled.h1`
  font-size: 32px;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  margin: 0 0 8px 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const ElegantSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 32px 0;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

// Navigation buttons for the panel
const PanelNavigationSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PanelNavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.1) 100%);
  color: #1e293b;
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  justify-content: flex-start;
  width: 100%;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.15) 100%);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.2);
  }
`;

const PanelNavTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Floating Mini Control Bar
const FloatingControlBar = styled.div<{ isVisible: boolean; isMobile: boolean }>`
  position: fixed;
  bottom: 30px;
  left: 30px;
  display: ${props => props.isMobile ? 'none' : 'flex'};
  gap: 12px;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: translateY(${props => props.isVisible ? '0' : '20px'});
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const FloatingControlButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 16px 24px;
  background: ${props => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' :
    props.variant === 'danger' ? 'linear-gradient(135deg, #ef4444, #dc2626)' :
    'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
  };
  color: ${props => props.variant === 'secondary' ? '#1e293b' : 'white'};
  border: none;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.025em;
  cursor: pointer;
  backdrop-filter: blur(20px);
  box-shadow: ${props => 
    props.variant === 'primary' ? '0 12px 32px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' :
    props.variant === 'danger' ? '0 12px 32px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' :
    '0 12px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
  };
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  border: 1px solid ${props => 
    props.variant === 'primary' ? 'rgba(59, 130, 246, 0.3)' :
    props.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' :
    'rgba(226, 232, 240, 0.6)'
  };
  
  /* Enhanced hover animations */
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    background: ${props => 
      props.variant === 'primary' ? 'linear-gradient(45deg, #2563eb, #1e40af)' :
      props.variant === 'danger' ? 'linear-gradient(45deg, #dc2626, #b91c1c)' :
      'linear-gradient(45deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 1))'
    };
  }
  
  &:active {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
  }
  
  /* Ripple effect */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:active::after {
    width: 300px;
    height: 300px;
  }
`;

const DesktopSidebar = styled.div`
  display: none;
`;

// Enhanced apartment display for floating panel
const SmartApartmentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 16px;
`;

const SmartApartmentCard = styled.div<{ available: boolean; isHovered: boolean }>`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid ${props => 
    props.isHovered ? 'rgba(255, 255, 255, 0.4)' : 
    props.available ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'
  };
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.available && `
    border-left: 3px solid #10b981;
  `}
  
  ${props => !props.available && `
    border-left: 3px solid #ef4444;
  `}
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.005em;
`;

const CardPrice = styled.div<{ available: boolean }>`
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: ${props => props.available ? '#10b981' : '#ef4444'};
  padding: 4px 8px;
  border-radius: 6px;
  background: ${props => props.available ? 
    'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'
  };
  border: 1px solid ${props => props.available ? 
    'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'
  };
  letter-spacing: 0.025em;
`;

const CardDetails = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;

const CardDetail = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;

  @media (max-width: 850px) {
    padding: 16px;
    order: 1;
  }
`;

const DesktopMainContent = styled.div`
  @media (min-width: 851px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1;
  }

  @media (max-width: 850px) {
    display: none;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 850px) {
    margin-bottom: 16px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 8px 0;

  @media (max-width: 850px) {
    font-size: 24px;
    margin: 0 0 6px 0;
  }
`;

const LogoImage = styled.img`
  height: 200px;
  width: auto;
  object-fit: contain;
  margin: 0 auto 8px auto;
  
  @media (max-width: 850px) {
    height: 80px;
    margin: 0 auto 6px auto;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6c757d;
  margin: 0;

  @media (max-width: 850px) {
    font-size: 14px;
  }
`;

const ViewerSection = styled.div`
  @media (min-width: 851px) {
    width: 100%;
    height: 100%;
    position: relative;
  }

  @media (max-width: 850px) {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
  }
`;

const FilterToggleButton = styled.button`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 32px);
    margin: 16px;
    padding: 16px 20px;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
  }
`;

const FilterSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 850px) {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    padding: 24px;
    margin: 20px;
    margin-bottom: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.3);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 20px;
      padding: 1px;
      background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: #ffffff;
  margin: 0 0 24px 0;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 850px) {
    font-size: 20px;
    font-weight: 800;
    font-family: 'Inter', sans-serif;
    color: #111827;
    margin: 0 0 20px 0;
    letter-spacing: -0.01em;
    text-align: center;
    
    &::after {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      border-radius: 2px;
      margin: 8px auto 0;
    }
  }
`;

const FilterRow = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 850px) {
    margin-bottom: 12px;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  
  @media (max-width: 850px) {
    color: #374151;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '•';
      color: #3b82f6;
      font-size: 18px;
      font-weight: 900;
    }
  }
`;

const CustomDropdownContainer = styled.div`
  position: relative;
`;

const CustomDropdownButton = styled.button<{ isOpen?: boolean }>`
  width: 100%;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.isOpen ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  font-size: 15px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid ${props => props.isOpen ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.3)'};
    color: #374151;
    padding: 16px 20px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
      border-radius: 16px;
      transition: opacity 0.3s ease;
      opacity: ${props => props.isOpen ? 1 : 0};
    }
    
    &:hover {
      border-color: rgba(59, 130, 246, 0.3);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.85));
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }

    &:focus {
      border-color: rgba(59, 130, 246, 0.5);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.9));
      outline: 2px solid rgba(59, 130, 246, 0.2);
      outline-offset: 2px;
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const DropdownIconWrapper = styled.div<{ isOpen?: boolean }>`
  width: 16px;
  height: 16px;
  color: #6c757d;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '▼';
    font-size: 12px;
  }
`;

const CustomDropdownMenu = styled.div<{ isOpen?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  margin-top: 8px;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.3s ease;
  max-height: 240px;
  overflow-y: auto;
  
  @media (max-width: 850px) {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    margin-top: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
    max-height: 280px;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb, #1e40af);
    }
  }
`;

const DropdownScrollIndicator = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    background: rgba(52, 152, 219, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #3498db;
    font-weight: 500;
    pointer-events: none;
    z-index: 1001;
    
    &::after {
      content: '⇅';
    }
  }
`;

const CustomDropdownOption = styled.button<{ isSelected?: boolean }>`
  width: 100%;
  padding: 12px 20px;
  background: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: ${props => props.isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
  font-weight: ${props => props.isSelected ? 500 : 400};
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  @media (max-width: 850px) {
    padding: 16px 20px;
    font-size: 15px;
    font-weight: ${props => props.isSelected ? 700 : 500};
    background: ${props => props.isSelected 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.05))' 
      : 'transparent'
    };
    color: ${props => props.isSelected ? '#1d4ed8' : '#374151'};
    border-radius: 12px;
    margin: 4px 8px;
    position: relative;
    
    ${props => props.isSelected && `
      &::before {
        content: '✓';
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #1d4ed8;
        font-weight: 700;
        font-size: 14px;
      }
    `}
    
    &:hover {
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(29, 78, 216, 0.02));
      color: #1f2937;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &:first-child,
    &:last-child {
      border-radius: 12px;
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const ToggleContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 16px 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    padding: 20px 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 16px;
      padding: 1px;
      background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
    
    &:hover {
      border-color: rgba(59, 130, 246, 0.3);
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.85));
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
    }

    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 850px) {
    color: #374151;
    font-size: 16px;
    font-weight: 600;
    gap: 12px;
  }
`;

const ToggleSwitch = styled.div<{ isChecked?: boolean }>`
  position: relative;
  width: 48px;
  height: 26px;
  background: ${props => props.isChecked 
    ? 'linear-gradient(135deg, #10b981, #059669)' 
    : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)'
  };
  border-radius: 13px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.isChecked 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(203, 213, 225, 0.6)'
  };

  &:before {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    background: linear-gradient(145deg, #ffffff, #f8fafc);
    border-radius: 50%;
    top: 2px;
    left: ${props => props.isChecked ? '24px' : '2px'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(226, 232, 240, 0.6);
  }
  
  @media (max-width: 850px) {
    width: 56px;
    height: 32px;
    border-radius: 16px;
    background: ${props => props.isChecked 
      ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
      : 'linear-gradient(135deg, #e5e7eb, #d1d5db)'
    };
    box-shadow: ${props => props.isChecked 
      ? '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
      : '0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
    };
    border: 1px solid ${props => props.isChecked 
      ? 'rgba(59, 130, 246, 0.3)' 
      : 'rgba(209, 213, 219, 0.6)'
    };

    &:before {
      width: 28px;
      height: 28px;
      top: 2px;
      left: ${props => props.isChecked ? '26px' : '2px'};
      background: linear-gradient(145deg, #ffffff, #f8fafc);
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(229, 231, 235, 0.8);
    }
    
    &:hover {
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  display: none;
`;

const ClearFiltersButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 850px) {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 16px;
    padding: 16px 24px;
    font-size: 16px;
    font-weight: 700;
    margin-top: 24px;
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }
    
    &:hover {
      background: linear-gradient(135deg, #dc2626, #b91c1c);
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
      
      &::before {
        left: 100%;
      }
    }

    &:active {
      background: linear-gradient(135deg, #b91c1c, #991b1b);
      transform: translateY(0) scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

const ResultsCount = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.05));
  backdrop-filter: blur(16px);
  color: #1e40af;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  margin-bottom: 24px;
  text-align: center;
  border: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 0.025em;

  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    color: #1d4ed8;
    border: 1px solid rgba(59, 130, 246, 0.2);
    border-radius: 16px;
    padding: 16px 20px;
    font-size: 16px;
    font-weight: 700;
    margin: 20px;
    margin-bottom: 20px;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3);
    position: relative;
    
    &::before {
      content: '';
    }
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 16px;
      padding: 1px;
      background: linear-gradient(145deg, rgba(59, 130, 246, 0.3), rgba(59, 130, 246, 0.1));
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
      pointer-events: none;
    }
  }
`;

const ApartmentListContainer = styled.div`
  @media (max-width: 850px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ApartmentInfo = styled.div`
  padding: 24px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ced4da;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #adb5bd;
  }

  @media (max-width: 850px) {
    padding: 16px;
    display: flex;
    flex-direction: column;
  }
`;

const ApartmentTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  color: white;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 850px) {
    font-size: 20px;
    background: linear-gradient(135deg, #0f172a, #1e293b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
`;

const ApartmentPrice = styled.div`
  font-size: 32px;
  font-weight: 800;
  font-family: 'Inter', sans-serif;
  color: white;
  margin: 0 0 20px 0;
  letter-spacing: -0.02em;

  @media (max-width: 850px) {
    font-size: 24px;
    margin: 0 0 12px 0;
    color: #1a1a1a; /* Schwarz für Mobile */
  }
`;

const ApartmentReservedPrice = styled.div`
  font-size: 32px;
  font-weight: 800;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 20px 0;
  letter-spacing: -0.02em;

  @media (max-width: 850px) {
    font-size: 24px;
    margin: 0 0 12px 0;
  }
`;

const ApartmentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 20px;
  margin: 24px 0;
  padding: 24px;
  background: linear-gradient(145deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8));
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.2);

  @media (max-width: 850px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin: 12px 0 16px;
    padding: 20px 16px;
    order: 1;
    background: linear-gradient(145deg, #ffffff, #f8fafc);
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const DetailItem = styled.div`
  text-align: center;
`;

const DetailIcon = styled.div`
  font-size: 20px;
  margin-bottom: 8px;
  color: #6c757d;
  display: flex;
  justify-content: center;

  @media (max-width: 850px) {
    font-size: 18px;
    margin-bottom: 6px;
  }
`;

const DetailLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.75px;

  @media (max-width: 850px) {
    font-size: 11px;
  }
`;

const DetailValue = styled.div`
  font-size: 17px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  color: #0f172a;
  letter-spacing: 0.025em;

  @media (max-width: 850px) {
    font-size: 14px;
  }
`;

const ApartmentDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #495057;
  margin: 20px 0;

  @media (max-width: 850px) {
    font-size: 14px;
    margin: 0 0 12px;
    order: 2;
    color: #475569;
    line-height: 1.55;
  }
`;

const ApartmentActions = styled.div`
  @media (max-width: 850px) {
    order: 5;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
  }
`;

const ApartmentFeatures = styled.div`
  margin-top: 20px;

  @media (max-width: 850px) {
    margin-top: 0;
    margin-bottom: 8px;
    order: 3;
  }
`;

const FeaturesTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;

  @media (max-width: 850px) {
    font-size: 16px;
    margin: 0 0 10px 0;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 850px) {
    gap: 6px;
  }
`;

const FeatureTag = styled.span`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05));
  backdrop-filter: blur(8px);
  color: #065f46;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  border: 1px solid rgba(16, 185, 129, 0.2);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
  letter-spacing: 0.025em;
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.08));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  }

  @media (max-width: 850px) {
    padding: 4px 8px;
    font-size: 12px;
  }
`;

const ContactButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #1e293b, #334155);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.025em;
  cursor: pointer;
  margin-top: 28px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 8px 24px rgba(30, 41, 59, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(30, 41, 59, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #334155, #475569);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(30, 41, 59, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(30, 41, 59, 0.3);
  }

  @media (max-width: 850px) {
    padding: 12px 16px;
    font-size: 15px;
    margin-top: 0;
  }
`;

const ShowApartmentButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.025em;
  cursor: pointer;
  margin-top: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #2563eb, #1e40af);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 850px) {
    padding: 12px 16px;
    font-size: 15px;
    margin-top: 0;

    &:hover {
      transform: none;
    }
  }
`;

const DownloadPDFButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  background: ${props => props.disabled 
    ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
    : 'linear-gradient(135deg, #10b981, #059669)'
  };
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.025em;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: ${props => props.disabled
    ? '0 4px 16px rgba(148, 163, 184, 0.2)'
    : '0 8px 24px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid ${props => props.disabled 
    ? 'rgba(148, 163, 184, 0.3)' 
    : 'rgba(16, 185, 129, 0.3)'
  };
  position: relative;
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    background: ${props => props.disabled 
      ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
      : 'linear-gradient(135deg, #059669, #047857)'
    };
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled 
      ? '0 4px 16px rgba(148, 163, 184, 0.2)' 
      : '0 12px 32px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
    };
    
    &::before {
      left: ${props => props.disabled ? '-100%' : '100%'};
    }
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
    box-shadow: ${props => props.disabled 
      ? '0 4px 16px rgba(148, 163, 184, 0.2)' 
      : '0 6px 20px rgba(16, 185, 129, 0.3)'
    };
  }

  @media (max-width: 850px) {
    padding: 12px 16px;
    font-size: 15px;
    margin-top: 0;

    &:hover {
      transform: none;
    }
  }
`;

const TypeformContainer = styled.div`
  width: 100%;
  max-height: 500px;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  
  @media (max-width: 850px) {
    max-height: 80vh;
    height: 80vh;
    min-height: 600px;
  }
`;

const ControlBar = styled.div`
  background: #2c3e50;
  border-radius: 8px;
  padding: 20px 24px;
  margin-top: 20px;
  box-shadow: 0 4px 12px rgba(44, 62, 80, 0.15);
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  border: 1px solid #34495e;

  @media (max-width: 850px) {
    padding: 16px;
    margin-top: 16px;
    border-radius: 8px;
    gap: 12px;
    justify-content: center;
  }
`;

const ControlButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.isActive ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: 1px solid ${props => props.isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  justify-content: center;
  
  &:hover {
    background: ${props => props.isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.15)'};
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 850px) {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 100px;
    gap: 6px;
    border-radius: 6px;

    &:hover {
      transform: none;
    }
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: inherit;

  @media (max-width: 850px) {
    width: 14px;
    height: 14px;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 8px;

  @media (max-width: 850px) {
    display: none;
  }
`;

const MobileToggleButton = styled.button`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    background: #3498db;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(52, 152, 219, 0.3);
    z-index: 100;
    transition: all 0.2s ease;

    &:hover {
      background: #2980b9;
      transform: scale(1.05);
    }
  }
`;



const ViewToggleButton = styled.button<{ isActive?: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
    : 'transparent'
  };
  color: ${props => props.isActive ? 'white' : '#6b7280'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.isActive 
      ? 'transparent' 
      : 'linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))'
    };
    border-radius: 12px;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.isActive 
      ? '0 6px 20px rgba(59, 130, 246, 0.2)' 
      : '0 3px 10px rgba(0, 0, 0, 0.05)'
    };
  }
  
  &:active {
    transform: translateY(0);
    transition: transform 0.1s ease;
  }
`;

const MobileBottomTabBar = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    padding: 12px 20px calc(12px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.08), 0 -1px 3px rgba(0, 0, 0, 0.1);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    z-index: 1000;
    gap: 8px;
  }
`;

const MobileTab = styled.button<{ isActive?: boolean }>`
  flex: 1;
  padding: 12px 8px;
  border: none;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-height: 60px;
  position: relative;
  overflow: hidden;
  
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
    : 'transparent'
  };
  color: ${props => props.isActive ? 'white' : '#6b7280'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.isActive 
      ? 'transparent' 
      : 'linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01))'
    };
    border-radius: 16px;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isActive 
      ? '0 8px 25px rgba(59, 130, 246, 0.25)' 
      : '0 4px 12px rgba(0, 0, 0, 0.08)'
    };
  }
  
  &:active {
    transform: translateY(-1px);
    transition: transform 0.1s ease;
  }
`;

const TabIcon = styled.div<{ isActive?: boolean }>`
  font-size: 20px;
  line-height: 1;
  transition: all 0.3s ease;
  transform: scale(${props => props.isActive ? 1.1 : 1});
`;

const TabLabel = styled.div<{ isActive?: boolean }>`
  font-size: 11px;
  font-weight: ${props => props.isActive ? '700' : '500'};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  opacity: ${props => props.isActive ? 1 : 0.8};
`;

const ClearMarkingsButton = styled.button`
  flex: 1;
  padding: 16px 20px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 60px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    transition: transform 0.1s ease;
  }
`;

const ClearIcon = styled.div`
  font-size: 20px;
  line-height: 1;
`;

// Floating Clear Button for when apartments are marked
const FloatingClearButton = styled.button`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    position: fixed;
    bottom: 20px;
    left: 20px;
    right: 20px;
    padding: 16px 20px;
    border: none;
    border-radius: 16px;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 600;
    letter-spacing: 0.025em;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(239, 68, 68, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    
    &:hover {
      background: linear-gradient(135deg, #dc2626, #b91c1c);
      transform: translateY(-2px);
      box-shadow: 0 6px 24px rgba(239, 68, 68, 0.4), 0 3px 10px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: translateY(0);
      transition: transform 0.1s ease;
    }
  }
`;

const ClearLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const MobileOverlayMenu = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: 16px;
    padding: 6px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 20px;
    margin-bottom: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    gap: 4px;
  }
`;

const MobileViewerContainer = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    flex-direction: column;
    position: relative;
    height: calc(100vh - 76px);
    max-height: calc(100vh - 76px);
    min-height: calc(100vh - 76px);
    width: 100%;
    background: white;
    overflow: hidden;
    box-sizing: border-box;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    overscroll-behavior: none;
  }
`;

const MobileApartmentList = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    position: relative;
    height: calc(100vh - 76px - 80px - env(safe-area-inset-bottom, 0px));
    /* Glassmorphic background */
    background: linear-gradient(135deg, 
      rgba(248, 250, 252, 0.9), 
      rgba(241, 245, 249, 0.8),
      rgba(226, 232, 240, 0.7)
    );
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 8px; /* Reduziert von 16px für mehr Breite */
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
    
    /* Add subtle pattern overlay */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
      background-size: 20px 20px;
      background-position: 0 0, 10px 10px;
      pointer-events: none;
      z-index: 0;
    }
    
    /* Ensure content is above the pattern */
    > * {
      position: relative;
      z-index: 1;
    }
  }
`;

// Custom Dropdown Component
const CustomDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  placeholder: string;
  label: string;
}> = ({ value, onChange, options, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(opt => opt.value === value);

  // Check if dropdown content is scrollable (Mobile only)
  useEffect(() => {
    if (isOpen && menuRef.current && window.innerWidth <= 768) {
      const element = menuRef.current;
      const isScrollable = element.scrollHeight > element.clientHeight;
      setShowScrollIndicator(isScrollable);
    } else {
      setShowScrollIndicator(false);
    }
  }, [isOpen, options]);

  return (
    <FilterRow>
      <FilterLabel>{label}</FilterLabel>
      <CustomDropdownContainer>
        <CustomDropdownButton
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedOption?.icon}{selectedOption?.icon ? ' ' : ''}{selectedOption?.label || placeholder}
          </span>
          <DropdownIconWrapper isOpen={isOpen} />
        </CustomDropdownButton>
        <CustomDropdownMenu ref={menuRef} isOpen={isOpen}>
          {options.map((option) => (
            <CustomDropdownOption
              key={option.value}
              isSelected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.icon}{option.icon ? ' ' : ''}{option.label}
            </CustomDropdownOption>
          ))}
          {showScrollIndicator && <DropdownScrollIndicator />}
        </CustomDropdownMenu>
      </CustomDropdownContainer>
    </FilterRow>
  );
};

// Custom Toggle Component
const CustomToggle: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  icon?: React.ReactNode;
}> = ({ checked, onChange, label, icon }) => {
  const handleToggle = () => {
    onChange(!checked);
  };

  return (
    <FilterRow>
      <ToggleContainer onClick={handleToggle}>
        <ToggleWrapper>
          <ToggleLabel>
            {icon}{icon ? ' ' : ''}{label}
          </ToggleLabel>
          <HiddenCheckbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          <ToggleSwitch 
            isChecked={checked}
          />
        </ToggleWrapper>
      </ToggleContainer>
    </FilterRow>
  );
};

interface ApartmentFinderProps {
  mobileView?: string;
  onMobileViewChange?: (view: string) => void;
  onViewToggle?: (isRealView: boolean, handleViewSwitch: () => void, standardImagesLoaded: boolean, realImagesLoaded: boolean) => void;
}

const ApartmentFinder: React.FC<ApartmentFinderProps> = ({ mobileView: externalMobileView, onMobileViewChange, onViewToggle }) => {
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);
  const [markedApartments, setMarkedApartments] = useState<Set<string>>(new Set());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isRealView, setIsRealView] = useState(true); // State für Ansicht wechseln - wird für mobile angepasst
  const [showTableView, setShowTableView] = useState(false); // State für Tabellen-/Listenansicht
  
  // Progressive Loading States
  const [realImagesLoaded, setRealImagesLoaded] = useState(false);
  const [standardImagesLoaded, setStandardImagesLoaded] = useState(false);
  const [isPreloadingSecondSet, setIsPreloadingSecondSet] = useState(false);

  const [selectedFloor, setSelectedFloor] = useState<string[]>([]);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [showReserved, setShowReserved] = useState<boolean>(false);
  const [hoveredApartment, setHoveredApartment] = useState<string | null>(null);
  const [showMobileViewerBackButton, setShowMobileViewerBackButton] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false); // Filter auf Mobile standardmäßig eingeklappt
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTypeformOpen, setIsTypeformOpen] = useState(false);
  // Use external mobile view state from App.tsx, fallback to internal state
  // Default to LIST view for better overview on mobile
  const [internalMobileView, setInternalMobileView] = useState(MOBILE_VIEW_LIST as any);
  const mobileView = externalMobileView || internalMobileView;
  const setMobileView = onMobileViewChange || setInternalMobileView;
  const [isRotating, setIsRotating] = useState(false);
  const [isTouchingWheel, setIsTouchingWheel] = useState(false);

  const [selectedApartmentsSVG, setSelectedApartmentsSVG] = useState<string[]>([]);
  const [isFullscreenFloorPlanOpen, setIsFullscreenFloorPlanOpen] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [svgOverlayVisible, setSvgOverlayVisible] = useState<boolean>(true);
  const [textSVGVisible, setTextSVGVisible] = useState<boolean>(true); // Text SVGs standardmäßig aktiviert
  
  // New loading and animation states
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // Dropdown states
  const [isFloorDropdownOpen, setIsFloorDropdownOpen] = useState(false);
  const [isRoomsDropdownOpen, setIsRoomsDropdownOpen] = useState(false);
  
  // Control bar states - removed all for now
  
  // Device detection using new hook
  const { deviceType, isMobile, isTablet, isDesktop } = useDeviceDetection();
  
  // Set mobile view to show "Nah Ansicht" (buildingImages) by default
  // SVG texts are always shown on mobile, and enabled by default on tablets and desktop
  // Mobile: show reserved apartments in the list by default
  useEffect(() => {
    if (isMobile) {
      setIsRealView(false); // false = buildingImages (Nah Ansicht mit 25 Bildern)
      setShowReserved(true);
      // SVG texts are always shown on mobile (no need to set textSVGVisible)
    }
    // Tablet und Desktop: textSVGVisible bleibt beim initialen State (true)
    // Kein explizites Setzen nötig, da der initiale State bereits true ist
  }, [isMobile]);

  // Hide mobile back button when leaving the 360° viewer tab
  useEffect(() => {
    if (isMobile && mobileView !== MOBILE_VIEW_VIEWER) {
      setShowMobileViewerBackButton(false);
    }
  }, [mobileView, isMobile]);

  // Auto-enable/disable SVG texts on desktop based on filter state
  // On tablets, SVG texts stay enabled by default but can still be toggled by filters
  useEffect(() => {
    if (isDesktop) {
      // Desktop: Texte standardmäßig immer aktiviert (wie auf Tablet)
      setTextSVGVisible(true); // Always keep enabled on desktop
    } else if (isTablet) {
      // On tablets, keep SVG texts enabled
      setTextSVGVisible(true); // Always keep enabled on tablets
    }
  }, [isDesktop, isTablet]);
  
  // Get current image set based on view state
  const currentBuildingImages = isRealView ? buildingRealImages : buildingImages;
  
  // Tablet uses same images as Desktop (optimiert für 50% schnellere Ladezeiten)
  // buildingImages: 25 Bilder (alle Geräte), buildingRealImages: 25 Bilder (alle Geräte)
  const getTabletBuildingImages = () => {
    if (!isTablet) return currentBuildingImages;
    return currentBuildingImages; // Use same as desktop - already optimized
  };
  
  // Progressive image preloading system
  const preloadImageSet = async (imageUrls: string[]): Promise<boolean> => {
    try {
      const loadPromises = imageUrls.map((url) => {
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => reject(new Error(`Failed to load ${url}`));
          img.src = url;
        });
      });
      
      await Promise.all(loadPromises);
      return true;
    } catch (error) {
      console.error('Error preloading images:', error);
      return false;
    }
  };
  
  // Preload Normale images first (primary set), then Nah images in background
  // Pass view toggle state up to App.tsx for navigation button
  useEffect(() => {
    onViewToggle?.(isRealView, handleViewSwitch, standardImagesLoaded, realImagesLoaded);
  }, [isRealView, standardImagesLoaded, realImagesLoaded]); // Removed onViewToggle from dependencies to prevent infinite loop

  useEffect(() => {
    const loadImages = async () => {
      try {
        // Mobile: Only load Nah Ansicht images (buildingImages - 25 Bilder)
        if (isMobile) {
          console.log('Mobile detected: Loading only Nah Ansicht images (buildingImages)...');
          const standardLoaded = await preloadImageSet(buildingImages);
          setStandardImagesLoaded(standardLoaded);
          
          if (standardLoaded) {
            console.log('Nah Ansicht images (buildingImages) loaded successfully for mobile');
          }
          return; // Skip loading real images on mobile
        }
        
        // Tablet & Desktop: Load both image sets progressively (same logic for both)
        // Step 1: Load Real images first (primary/default view)
        console.log('Loading Real images (primary set)...');
        const realLoaded = await preloadImageSet(buildingRealImages);
        setRealImagesLoaded(realLoaded);
        
        if (realLoaded) {
          console.log('Real images loaded successfully');
          
          // Step 2: Start preloading Nah images in background after a short delay
          setTimeout(async () => {
            setIsPreloadingSecondSet(true);
            console.log('Starting background preload of Nah images...');
            
            const standardLoaded = await preloadImageSet(buildingImages);
            setStandardImagesLoaded(standardLoaded);
            setIsPreloadingSecondSet(false);
            
            if (standardLoaded) {
              console.log('Nah images preloaded successfully in background');
            }
          }, 500); // 500ms delay to ensure smooth user experience
        }
      } catch (error) {
        console.error('Error in progressive loading:', error);
      }
    };
    
    loadImages();
  }, [isMobile, isTablet]); // Re-run when device type changes
  
  // Handler für Ansicht wechseln mit Bildposition-Synchronisation
  const handleViewSwitch = () => {
    const newIsRealView = !isRealView;
    
    // Check if target image set is loaded
    const targetImagesLoaded = newIsRealView ? realImagesLoaded : standardImagesLoaded;
    
    if (!targetImagesLoaded) {
      console.log('Target image set not yet loaded, please wait...');
      // Optionally show a loading indicator here
      return;
    }
    
    // Tablet & Desktop use same logic (both have 25 Real images)
    if (newIsRealView) {
      // Wechsel von Nah zu Normale: Finde nächstmögliches Bild
      const newIndex = convertStandardToRealIndex(currentImageIndex);
      setCurrentImageIndex(newIndex);
    } else {
      // Wechsel von Normale zu Nah: Konvertiere zurück
      const newIndex = convertRealToStandardIndex(currentImageIndex);
      setCurrentImageIndex(newIndex);
    }
    
    setIsRealView(newIsRealView);
  };

  const handleTableViewToggle = () => {
    setShowTableView(!showTableView);
  };
  
  // NEW: Revolutionary Desktop Panel States
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  
  const { apartments } = useApartments();

  // Get unique floor levels and room counts for filter options
  const floorLevels = Array.from(new Set(apartments.map(apt => apt.floor))).sort((a, b) => a - b);
  const roomCounts = Array.from(new Set(apartments.map(apt => apt.rooms))).sort((a, b) => a - b);

  // Show all apartments (both available and reserved)
  const allApartments = apartments;

  // Mapping function to convert SVG apartment IDs (A1, A2, B1, etc.) to data IDs (1, 2, 12, etc.)
  const mapSVGIdToDataId = (svgId: string): number | null => {
    const match = svgId.match(/^([AB])(\d+)$/);
    if (!match) return null;
    
    const [, building, apartmentNum] = match;
    const num = parseInt(apartmentNum, 10);
    
    if (building === 'A') {
      return num; // A1-A11 maps to IDs 1-11
    } else if (building === 'B') {
      return num + 11; // B1-B11 maps to IDs 12-22
    }
    
    return null;
  };

  // Get apartment name from SVG ID
  const getApartmentNameFromSVGId = (svgId: string): string => {
    const dataId = mapSVGIdToDataId(svgId);
    if (!dataId) return `Wohnung ${svgId}`;
    
    const apartment = apartments.find(apt => apt.id === dataId);
    return apartment ? apartment.name : `Wohnung ${svgId}`;
  };

  // Mapping function to convert data IDs (1, 2, 12, etc.) to SVG apartment IDs (A1, A2, B1, etc.)
  const mapDataIdToSVGId = (dataId: number): string | null => {
    if (dataId >= 1 && dataId <= 11) {
      return `A${dataId}`;
    } else if (dataId >= 12 && dataId <= 22) {
      return `B${dataId - 11}`;
    }
    return null;
  };

  // Get data IDs for selected SVG apartments
  const getSelectedDataIds = (): number[] => {
    return selectedApartmentsSVG
      .map(svgId => mapSVGIdToDataId(svgId))
      .filter(dataId => dataId !== null) as number[];
  };

  // Helper function to get apartment status (with backward compatibility)
  const getApartmentStatus = (apartment: any): string => {
    if (apartment.status) {
      return apartment.status;
    }
    // Backward compatibility: convert available boolean to status
    return apartment.available ? 'available' : 'reserved';
  };

  // Base filtered apartments (without SVG selection) - used for 360° viewer
  const baseFilteredApartments = allApartments.filter(apartment => {
    const apartmentStatus = getApartmentStatus(apartment);
    const matchesAvailability = showReserved
      ? apartmentStatus !== 'sold'
      : apartmentStatus === 'available';

    // Floor filter - check if apartment floor is in selected floors array
    const matchesFloor = selectedFloor.length === 0 || selectedFloor.includes(apartment.floor.toString());

    // Room count filter - check if apartment rooms is in selected rooms array
    const matchesRooms = selectedRooms.length === 0 || selectedRooms.includes(apartment.rooms.toString());

    return matchesAvailability && matchesFloor && matchesRooms;
  });

  // Full filtered apartments (with SVG selection) - used for apartment list
  const filteredApartments = baseFilteredApartments.filter(apartment => {
    // SVG selection filter - if any SVG apartments are selected, only show those
    const selectedDataIds = getSelectedDataIds();
    if (selectedDataIds.length > 0) {
      const matchesSVGSelection = selectedDataIds.includes(apartment.id);
      if (!matchesSVGSelection) return false;
    }

    return true;
  });

  const handleApartmentSelect = (apartment: Apartment) => {
    // Close table view if it's open
    if (showTableView) {
      setShowTableView(false);
    }
    
    // Add loading animation for mobile
    if (window.innerWidth <= 768) {
      setIsLoading(true);
      
      // Simulate loading progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setLoadingProgress(progress);
        
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setSelectedApartment(apartment);
            setIsModalOpen(true);
            setIsLoading(false);
            setLoadingProgress(0);
          }, 200);
        }
      }, 50);
    } else {
      setSelectedApartment(apartment);
      setIsModalOpen(true);
    }
  };

  // Download floor plan PDF function for desktop modal
  const downloadFloorPlanPDF = async (apartment: Apartment) => {
    try {
      setIsDownloadingPDF(true);
      
      // Generate PDF URL using the new naming convention (e.g., Wohnung A1.1.pdf)
      const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
      const pdfName = getApartmentPDFName(apartment.id);
      const pdfUrl = `${cloudfrontUrl}/PDF-Grundrisse/${pdfName}.pdf`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${pdfName}.pdf`;
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading floor plan:', error);
      alert('Fehler beim Herunterladen des Grundrisses. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleContactRequest = (apartment: Apartment) => {
    // Add smooth transition
    setIsTransitioning(true);
    setTimeout(() => {
      setIsTypeformOpen(true);
      setIsTransitioning(false);
    }, 200);
  };

  const handleShowApartment = (apartment: Apartment) => {
    const svgId = mapDataIdToSVGId(apartment.id);
    if (svgId) {
      // Clear current SVG selection and select only this apartment
      setSelectedApartmentsSVG([svgId]);
      // Close the modal
      setIsModalOpen(false);
      
      // On mobile: switch to 360° viewer and show back button
      if (isMobile) {
        setShowMobileViewerBackButton(true);
        if (mobileView === MOBILE_VIEW_LIST) {
          setMobileView(MOBILE_VIEW_VIEWER);
        }
      }
      
      // Open the panel (this should only happen via this button)
      setIsPanelOpen(true);
    }
  };

  const handleMobileBackToDetails = () => {
    setShowMobileViewerBackButton(false);
    setMobileView(MOBILE_VIEW_LIST);
    if (selectedApartment) {
      setIsModalOpen(true);
    }
  };

  // Handle tablet apartment show click from tooltip - similar to handleShowApartment but from SVG ID
  const handleTabletApartmentShowClick = (svgId: string) => {
    console.log('🎯 handleTabletApartmentShowClick called for:', svgId);
    // Convert SVG ID to data ID to find the apartment
    const dataId = mapSVGIdToDataId(svgId);
    
    if (dataId) {
      const apartment = allApartments.find(apt => apt.id === dataId);
      
      if (apartment) {
        // Tablet: Use same multi-select logic as Desktop
        setSelectedApartmentsSVG(prev => {
          // In multi-select mode: allow toggle behavior (add/remove)
          if (isMultiSelectMode) {
            if (prev.includes(svgId)) {
              // Remove from selection
              console.log('📂 Tablet: Removing apartment from selection:', svgId);
              return prev.filter(id => id !== svgId);
            } else {
              // Add to selection
              console.log('📂 Tablet: Adding apartment to selection:', svgId);
              return [...prev, svgId];
            }
          } else {
            // In normal mode: replace selection with this apartment
            console.log('📂 Tablet: Replacing selection with apartment:', svgId);
            return [svgId];
          }
        });
        
        // Open the panel directly (like desktop "Wohnung im Gebäude zeigen" button)
        console.log('📂 Tablet: Opening panel');
        setIsPanelOpen(true);
      }
    }
  };

  const handleCloseTypeform = () => {
    setIsTypeformOpen(false);
  };

  const handleFullscreenFloorPlanOpen = () => {
    setIsFullscreenFloorPlanOpen(true);
  };

  // Toggle text SVG visibility only
  const toggleSVGOverlay = () => {
    setTextSVGVisible(prev => !prev);
  };

  const handleFullscreenFloorPlanClose = () => {
    setIsFullscreenFloorPlanOpen(false);
  };

  const clearFilters = () => {
    setSelectedFloor([]);
    setSelectedRooms([]);
    setShowReserved(false);
  };

  const getFloorLabel = (floor: number) => {
    if (floor === 0) return 'EG';
    if (floor === 3) return 'Attika';
    return `${floor}. Obergeschoss`;
  };

  // Zentrale Preisformatierung wird jetzt verwendet

  const formatSize = (size: number) => {
    return `${size} m²`;
  };

  const getCleanApartmentName = (name: string) => {
    // Robuste Extraktion des Apartment-Identifiers
    
    // Zuerst versuchen: "Wohnung A1 - ..." Pattern
    let match = name.match(/Wohnung ([AB]\d+)/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Fallback: Falls nur "A1", "B5" etc.
    match = name.match(/^([AB]\d+)$/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Fallback: Falls der Name bereits "A1" oder ähnlich ist, füge "Wohnung" hinzu
    match = name.match(/^([AB]\d+)(.*)$/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Letzter Fallback: Originalname zurückgeben
    return name.startsWith('Wohnung') ? name : `Wohnung ${name}`;
  };

  // Apartment interaction handlers for SVG overlays
  const handleApartmentClick = (apartmentId: string) => {
    // On Desktop: SVG click should open the side panel with apartment list
    console.log('🎯 handleApartmentClick called for:', apartmentId, 'Device width:', window.innerWidth);
    
    setSelectedApartmentsSVG(prev => {
      // In multi-select mode: allow toggle behavior (add/remove)
      if (isMultiSelectMode) {
        if (prev.includes(apartmentId)) {
          // Remove from selection
          return prev.filter(id => id !== apartmentId);
        } else {
          // Add to selection
          return [...prev, apartmentId];
        }
      } else {
        // In normal mode: replace selection with this apartment
        return [apartmentId];
      }
    });
    
    // Open the panel to show the filtered apartment list
    console.log('📂 Opening panel, isPanelOpen before:', isPanelOpen);
    setIsPanelOpen(true);
    console.log('📂 Panel should now be open');
  };



  // Revolutionary Desktop Panel Functions
  const togglePanel = () => {
    // Wenn Panel geschlossen wird, Auswahl automatisch löschen
    if (isPanelOpen) {
      setSelectedApartmentsSVG([]);
      setSelectedApartment(null);
    }
    setIsPanelOpen(!isPanelOpen);
  };

  const toggleMultiSelect = () => {
    if (isMultiSelectMode) {
      disableMultiSelect();
    } else {
      enableMultiSelect();
    }
  };



  // Handle SVG apartment clicks - Tablet/Mobile behavior (no auto-open)
  const handleSVGApartmentClick = (svgId: string) => {
    // Don't auto-open panel - only show tooltip
    // Panel should only open via "Wohnung im Gebäude zeigen" button
    // This is used for Tablet and Mobile devices
    
    setSelectedApartmentsSVG(prev => {
      // In multi-select mode: allow toggle behavior (add/remove)
      if (isMultiSelectMode) {
        if (prev.includes(svgId)) {
          // Remove from selection
          return prev.filter(id => id !== svgId);
        } else {
          // Add to selection
          return [...prev, svgId];
        }
      } else {
        // In normal mode: always select (no toggle - apartment stays selected)
        if (prev.includes(svgId)) {
          // Already selected - keep it selected (no toggle)
          return prev;
        } else {
          // Not selected - replace selection with this apartment
          return [svgId];
        }
      }
    });
  };

  // Handle tablet apartment click events
  useEffect(() => {
    const handleTabletClick = (event: CustomEvent) => {
      const { apartmentId } = event.detail;
      handleSVGApartmentClick(apartmentId);
    };

    window.addEventListener('tabletApartmentClick', handleTabletClick as EventListener);
    
    return () => {
      window.removeEventListener('tabletApartmentClick', handleTabletClick as EventListener);
    };
  }, []);

  // Handle SVG apartment selection for mobile (always replace, never toggle)
  const handleMobileSVGApartmentSelect = (svgId: string) => {
    // Always replace the selection with the new apartment
    setSelectedApartmentsSVG([svgId]);
  };

  // Handle mobile apartment clicks (always replace, never toggle)
  const handleMobileApartmentClick = (apartmentId: string) => {
    // Always replace the selection with the new apartment
    setSelectedApartmentsSVG([apartmentId]);
  };

  // Handle mobile apartment details button click from tooltip (opens modal directly)
  const handleMobileApartmentDetails = (apartmentId: string) => {
    // Convert SVG apartment ID (A1, B2, etc.) to data ID if needed
    let searchId = apartmentId;
    
    // Check if it's an SVG ID format (A1, A2, B1, etc.)
    const svgMatch = apartmentId.match(/^([AB])(\d+)$/);
    if (svgMatch) {
      const [, building, apartmentNum] = svgMatch;
      const num = parseInt(apartmentNum, 10);
      
      if (building === 'A') {
        searchId = num.toString(); // A1-A11 maps to IDs 1-11
      } else if (building === 'B') {
        searchId = (num + 11).toString(); // B1-B11 maps to IDs 12-22
      }
    }
    
    // Find apartment by converted ID and open modal directly
    const apartment = apartments.find(apt => apt.id.toString() === searchId);
    if (apartment) {
      handleApartmentSelect(apartment); // This opens the modal
    }
  };

  // Clear SVG apartment selection
  const clearSVGSelection = () => {
    setSelectedApartmentsSVG([]);
    setSelectedApartment(null); // Clear normal apartment selection too
    setIsModalOpen(false); // Close modal if open
    setIsMultiSelectMode(false); // Also disable multi-select mode
  };

  const handleApartmentHover = (apartmentId: string | null) => {
    setHoveredApartment(apartmentId);
  };

  const handleApartmentCardHover = (apartment: Apartment | null) => {
    if (apartment) {
      // Convert data ID to SVG ID format (reverse of mapSVGIdToDataId)
      const dataId = apartment.id;
      let svgId = null;
      
      if (dataId <= 11) {
        // Building A
        svgId = `A${dataId}`;
      } else {
        // Building B  
        svgId = `B${dataId - 11}`;
      }
      
      console.log('🏠 Card hover - Apartment:', apartment.name, 'Data ID:', dataId, 'SVG ID:', svgId);
      setHoveredApartment(svgId);
    } else {
      console.log('🏠 Card hover cleared');
      setHoveredApartment(null);
    }
  };

  // Mobile-specific: Clear all SVG selections
  const clearAllSVGSelection = () => {
    setSelectedApartmentsSVG([]);
  };

  // Handle marked apartments changes from Viewer360
  const handleMarkedApartmentsChange = (newMarkedApartments: Set<string>) => {
    setMarkedApartments(newMarkedApartments);
  };

  // Handle clear all markings
  const handleClearAllMarkings = () => {
    setMarkedApartments(new Set());
  };

  // Add state for multi-select mode
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // Enable multi-select mode
  const enableMultiSelect = () => {
    setIsMultiSelectMode(true);
    
    // If there's a currently selected apartment but no SVG selection, 
    // add it to SVG selection for multi-select mode
    if (selectedApartment && selectedApartmentsSVG.length === 0) {
      const svgId = mapDataIdToSVGId(selectedApartment.id);
      if (svgId) {
        setSelectedApartmentsSVG([svgId]);
      }
    }
  };

  // Disable multi-select mode
  const disableMultiSelect = () => {
    setIsMultiSelectMode(false);
  };

  // Handle individual apartment deselection from the list
  const handleApartmentDeselect = (apartment: Apartment) => {
    // Convert apartment data ID to SVG ID
    const svgId = mapDataIdToSVGId(apartment.id);
    if (svgId) {
      setSelectedApartmentsSVG(prev => prev.filter(id => id !== svgId));
    }
  };



  // Generate apartment colors for SVG overlay based on status
  const apartmentColors: Record<string, string> = {};
  apartments.forEach((apartment) => {
    // Convert apartment data ID to SVG ID for mapping
    const svgId = mapDataIdToSVGId(apartment.id);
    if (svgId) {
      // Get apartment status with backward compatibility
      const getApartmentStatus = (apt: any) => {
        if (apt.status) {
          return apt.status;
        }
        // Backward compatibility: convert available boolean to status
        return apt.available !== false ? 'available' : 'sold';
      };

      const status = getApartmentStatus(apartment);
      
      // Color mapping based on status
      switch (status) {
        case 'available':
          apartmentColors[svgId] = '#22c55e'; // Green for available
          break;
        case 'reserved':
          apartmentColors[svgId] = '#f59e0b'; // Orange for reserved
          break;
        case 'sold':
          apartmentColors[svgId] = '#ef4444'; // Red for sold
          break;
        default:
          apartmentColors[svgId] = '#22c55e'; // Default to green
      }
    }
  });

  // Control bar handlers removed for now

  const handleRotationChange = (rotating: boolean) => {
    setIsRotating(rotating);
  };

  const handleTouchStateChange = (isTouching: boolean) => {
    setIsTouchingWheel(isTouching);
  };

  // Device-specific body overflow control
  useEffect(() => {
    // Control body overflow for mobile and tablet
    if (isMobile || isTablet) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100vh';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.height = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.height = 'auto';
    };
  }, [isMobile, isTablet]);

  // Additional effect to prevent scrolling specifically in mobile 360° view
  useEffect(() => {
    if (isMobile && mobileView === MOBILE_VIEW_VIEWER) {
      // Extra scroll prevention for 360° view
      document.body.style.touchAction = 'none';
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      
      // Prevent default on touch events
      const preventTouch = (e: TouchEvent) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchstart', preventTouch, { passive: false });
      document.addEventListener('touchmove', preventTouch, { passive: false });
      
      return () => {
        document.body.style.touchAction = 'auto';
        document.body.style.userSelect = 'auto';
        document.body.style.webkitUserSelect = 'auto';
        document.removeEventListener('touchstart', preventTouch);
        document.removeEventListener('touchmove', preventTouch);
      };
    }
  }, [isMobile, mobileView]);

  // Global scroll prevention for mobile 360° view
  useEffect(() => {
    if (isMobile && mobileView === MOBILE_VIEW_VIEWER) {
      // Prevent all scrolling on mobile 360° view
      const preventScroll = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      const preventWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      const preventKeyScroll = (e: KeyboardEvent) => {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
        if (scrollKeys.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      // Add event listeners
      document.addEventListener('scroll', preventScroll, { passive: false });
      document.addEventListener('wheel', preventWheel, { passive: false });
      document.addEventListener('keydown', preventKeyScroll, { passive: false });
      window.addEventListener('scroll', preventScroll, { passive: false });
      
      return () => {
        document.removeEventListener('scroll', preventScroll);
        document.removeEventListener('wheel', preventWheel);
        document.removeEventListener('keydown', preventKeyScroll);
        window.removeEventListener('scroll', preventScroll);
      };
    }
  }, [isMobile, mobileView]);

  // Initialize Typeform when modal opens
  useEffect(() => {
    if (isTypeformOpen && window.tf && window.tf.load) {
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        window.tf?.load?.();
      }, 100);
    }
  }, [isTypeformOpen]);




  if (isMobile) {
    // Mobile Layout with conditional 360° viewer
    return (
      <Container mobileView={mobileView}>
        {/* 360° Viewer - only show when 360° Ansicht tab is active */}
        {mobileView === MOBILE_VIEW_VIEWER && (
          <MobileViewerContainer>
            <Viewer360
              key={`mobile-viewer-${mobileView}-${isRealView}`}
              images={currentBuildingImages}
              currentIndex={currentImageIndex}
              onIndexChange={setCurrentImageIndex}
              onRotationChange={handleRotationChange}
              onTouchStateChange={handleTouchStateChange}
              onApartmentClick={isMobile ? handleMobileApartmentClick : handleApartmentClick}
              onApartmentSVGSelect={isMobile ? handleMobileSVGApartmentSelect : handleSVGApartmentClick}
              onApartmentSVGClear={clearAllSVGSelection}
              onMobileApartmentDetails={handleMobileApartmentDetails}
              showMobileBackButton={showMobileViewerBackButton}
              onMobileBackToDetails={handleMobileBackToDetails}
              onTabletApartmentListClick={handleSVGApartmentClick}
              onApartmentHover={handleApartmentHover}
              externalHoveredApartment={hoveredApartment}
              apartmentColors={apartmentColors}
              selectedApartments={selectedApartmentsSVG}
              apartments={baseFilteredApartments}
              isMultiSelectMode={isMultiSelectMode}
              mobileOverlayMenu={null}
              onMarkedApartmentsChange={handleMarkedApartmentsChange}
              onClearAllMarkings={handleClearAllMarkings}
              isRealView={isRealView}
            />
          </MobileViewerContainer>
        )}

        {/* Clear markings button - now floating when needed */}
        {markedApartments.size > 0 && (
          <FloatingClearButton onClick={handleClearAllMarkings}>
            <ClearIcon><Trash2 size={16} /></ClearIcon>
            <ClearLabel>Alle Markierungen löschen ({markedApartments.size})</ClearLabel>
          </FloatingClearButton>
        )}

        {/* Apartment list - only show when Wohnungen tab is active */}
        {mobileView === MOBILE_VIEW_LIST && (
          <MobileApartmentList>
            
            {/* Loading Overlay */}
            {isLoading && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '20px'
              }}>
                <div style={{
                  width: '300px',
                  maxWidth: '80%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}>
                    <div style={{
                      width: `${loadingProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                      boxShadow: '0 0 10px rgba(59, 130, 246, 0.3)'
                    }} />
                  </div>
                  <p style={{
                    color: '#1f2937',
                    fontSize: '24px',
                    fontWeight: '700',
                    fontFamily: 'Inter, sans-serif',
                    margin: 0
                  }}>
                    {Math.round(loadingProgress)}%
                  </p>
                </div>
                <p style={{
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Lade Wohnungsdetails...
                </p>
              </div>
            )}

            {/* Filter Toggle Button - nur auf Mobile */}
            {selectedApartmentsSVG.length === 0 && (
              <FilterToggleButton onClick={() => setIsFilterOpen(!isFilterOpen)}>
                <span>Filter Wohnungsliste</span>
                <Filter size={20} />
              </FilterToggleButton>
            )}

            {/* Glassmorphic Filter Panel - only show when no SVG selection is active and filter is open */}
            {selectedApartmentsSVG.length === 0 && isFilterOpen && (
              <PureGlassmorphicFilterPanel
                selectedFloor={selectedFloor}
                selectedRooms={selectedRooms}
                showReserved={showReserved}
                floorOptions={[
                  { value: '', label: 'Alle Stockwerke' },
                  ...floorLevels.map(floor => ({
                    value: floor.toString(),
                    label: getFloorLabel(floor)
                  }))
                ]}
                roomOptions={[
                  { value: '', label: 'Alle Zimmeranzahlen' },
                  ...roomCounts.map(rooms => ({
                    value: rooms.toString(),
                    label: `${rooms} Zimmer`
                  }))
                ]}
                onFloorChange={setSelectedFloor}
                onRoomsChange={setSelectedRooms}
                onShowReservedToggle={setShowReserved}
                onClearFilters={clearFilters}
                resultCount={filteredApartments.length}
                totalCount={apartments.length}
              />
            )}

            {/* SVG Selection Section */}
            {selectedApartmentsSVG.length > 0 && (
              <FilterSection>
                <SectionTitle>
                  {selectedApartmentsSVG.length === 1 ? 'Ausgewählte Wohnung' : 'Ausgewählte Wohnungen'} ({selectedApartmentsSVG.length})
                  {isMultiSelectMode && !isMobile && <span style={{ color: '#27ae60', fontSize: '12px', marginLeft: '8px' }}>• Multi-Auswahl aktiv</span>}
                </SectionTitle>
                <div style={{ fontSize: '18px', color: '#ffffff', marginBottom: '12px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }} className="apartment-selection-display">
                  {selectedApartmentsSVG.map(id => getApartmentNameFromSVGId(id)).join(', ')}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!isMultiSelectMode && !isMobile && (
                    <ClearFiltersButton 
                      onClick={enableMultiSelect}
                      style={{ 
                        background: '#27ae60', 
                        color: 'white',
                        flex: selectedApartmentsSVG.length === 1 ? '1' : 'none'
                      }}
                    >
                      Weitere Wohnungen auswählen
                    </ClearFiltersButton>
                  )}
                  {isMultiSelectMode && !isMobile && (
                    <ClearFiltersButton 
                      onClick={disableMultiSelect}
                      style={{ 
                        background: '#6c757d', 
                        color: 'white'
                      }}
                    >
                      Auswahl beenden
                    </ClearFiltersButton>
                  )}
                  <ClearFiltersButton onClick={clearSVGSelection}>
                    Auswahl aufheben
                  </ClearFiltersButton>
                </div>
              </FilterSection>
            )}

            {/* Glassmorphic Apartment List */}
            <div style={{ padding: '0 8px 16px 8px' }}>
              <ApartmentList
                apartments={filteredApartments}
                onApartmentSelect={handleApartmentSelect}
                selectedApartment={selectedApartment}
                onApartmentHover={handleApartmentCardHover}
              />
            </div>
          </MobileApartmentList>
        )}

        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          {selectedApartment && (
            <ApartmentInfo>
              <ApartmentTitle>{selectedApartment.name}</ApartmentTitle>
              {selectedApartment.available ? (
                <ApartmentPrice>{formatPrice(selectedApartment.price)}</ApartmentPrice>
              ) : (
                <ApartmentReservedPrice>Reserviert</ApartmentReservedPrice>
              )}
              
              <ApartmentDetails>
                <DetailItem>
                  <DetailIcon><Building2 size={16} /></DetailIcon>
                  <DetailLabel>Größe</DetailLabel>
                  <DetailValue>{formatSize(selectedApartment.size)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><BedDouble size={16} /></DetailIcon>
                  <DetailLabel>Zimmer</DetailLabel>
                  <DetailValue>{selectedApartment.rooms}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><ArrowUp size={16} /></DetailIcon>
                  <DetailLabel>Etage</DetailLabel>
                  <DetailValue>{getFloorLabel(selectedApartment.floor)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><Info size={16} /></DetailIcon>
                  <DetailLabel>Bezug ab</DetailLabel>
                  <DetailValue>{selectedApartment.constructionYear || 'N/A'}</DetailValue>
                </DetailItem>
              </ApartmentDetails>
              
              <ApartmentDescription>
                {selectedApartment.description}
              </ApartmentDescription>
              
              <ApartmentFeatures>
                <FeaturesList>
                  {selectedApartment.features.map((feature, index) => (
                    <FeatureTag key={index}>{feature}</FeatureTag>
                  ))}
                </FeaturesList>
              </ApartmentFeatures>

              {selectedApartment.floorPlan && (
                <FloorPlanViewer
                  floorPlan={selectedApartment.floorPlan}
                  floorPlanImages={selectedApartment.floorPlanImages}
                  apartmentName={selectedApartment.name}
                  onFullscreenOpen={handleFullscreenFloorPlanOpen}
                />
              )}

              <ApartmentActions>
                <ContactButton onClick={() => handleContactRequest(selectedApartment)}>
                  Kontakt aufnehmen
                </ContactButton>
                <DownloadPDFButton 
                  onClick={() => downloadFloorPlanPDF(selectedApartment)}
                  disabled={isDownloadingPDF}
                >
                  {isDownloadingPDF ? 'Lädt herunter...' : 'Grundriss Herunterladen'}
                </DownloadPDFButton>
                <ShowApartmentButton onClick={() => handleShowApartment(selectedApartment)}>
                  Wohnung im Gebäude zeigen
                </ShowApartmentButton>
              </ApartmentActions>
            </ApartmentInfo>
          )}
        </Modal>
        
        <Modal isOpen={isTypeformOpen} onClose={handleCloseTypeform}>
          <TypeformContainer>
            <div data-tf-live="01K0NRBGCX3AZ9EJB04NFS8A39"></div>
          </TypeformContainer>
        </Modal>

        {selectedApartment && selectedApartment.floorPlan && (
          <FullscreenFloorPlanViewer
            floorPlan={selectedApartment.floorPlan}
            floorPlanImages={selectedApartment.floorPlanImages}
            apartmentName={selectedApartment.name}
            isOpen={isFullscreenFloorPlanOpen}
            onClose={handleFullscreenFloorPlanClose}
          />
        )}
      </Container>
    );
  }

  // Tablet Layout - similar to desktop but with touch optimizations
  if (isTablet) {
    return (
      <Container>
        {/* Tablet gets fullscreen 360° viewer like desktop */}
        <Viewer360
          key={`tablet-viewer-${isRealView}`}
          images={getTabletBuildingImages()}
          currentIndex={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
          onRotationChange={handleRotationChange}
          onTouchStateChange={handleTouchStateChange}
          onApartmentClick={handleSVGApartmentClick}
          onApartmentSVGSelect={handleSVGApartmentClick}
          onApartmentSVGClear={clearAllSVGSelection}
          onMobileApartmentDetails={handleMobileApartmentDetails}
          onTabletApartmentListClick={handleTabletApartmentShowClick}
          onApartmentHover={handleApartmentHover}
          externalHoveredApartment={hoveredApartment}
          apartmentColors={apartmentColors}
          selectedApartments={selectedApartmentsSVG}
          apartments={baseFilteredApartments}
          isMultiSelectMode={isMultiSelectMode}
          onMarkedApartmentsChange={handleMarkedApartmentsChange}
          onClearAllMarkings={handleClearAllMarkings}
          isRealView={isRealView}
          textSVGVisible={textSVGVisible}
          showTextOverlay={true}
          svgOverlayVisible={svgOverlayVisible}
        />

        {/* Elegant Sidebar for tablets - same as desktop */}
        {!showTableView && (
          <ElegantSidebar isOpen={isPanelOpen}>
            <SidebarContent>
              <SidebarHeader>
                <ElegantTitle>Wildbach</ElegantTitle>
                <ElegantSubtitle>
                  {apartments.filter(apt => apt.available).length} von {apartments.length} verfügbar
                </ElegantSubtitle>
              </SidebarHeader>

              {/* Filters - only show when no SVG selection */}
              {selectedApartmentsSVG.length === 0 && (
                <PureGlassmorphicFilterPanel
                  selectedFloor={selectedFloor}
                  selectedRooms={selectedRooms}
                  showReserved={showReserved}
                  floorOptions={[
                    { value: '', label: 'Alle Stockwerke' },
                    ...floorLevels.map(floor => ({
                      value: floor.toString(),
                      label: getFloorLabel(floor)
                    }))
                  ]}
                  roomOptions={[
                    { value: '', label: 'Alle Zimmeranzahlen' },
                    ...roomCounts.map(rooms => ({
                      value: rooms.toString(),
                      label: rooms === 4 ? '4+' : `${rooms}`
                    }))
                  ]}
                  onFloorChange={setSelectedFloor}
                  onRoomsChange={setSelectedRooms}
                  onShowReservedToggle={setShowReserved}
                  onClearFilters={clearFilters}
                  resultCount={filteredApartments.length}
                  totalCount={apartments.length}
                />
              )}

              {/* SVG Selection Management */}
              {selectedApartmentsSVG.length > 0 && (
                <div>
                  <SectionTitle>
                    Ausgewählt ({selectedApartmentsSVG.length})
                    {isMultiSelectMode && <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginLeft: '8px' }}>• Multi aktiv</span>}
                  </SectionTitle>
                  <div style={{ fontSize: '18px', color: '#ffffff', marginBottom: '24px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
                    {selectedApartmentsSVG.map(id => getApartmentNameFromSVGId(id)).join(', ')}
                  </div>
                </div>
              )}

              {/* Glassmorphic Smart Apartment Display - same as desktop */}
              <div className="space-y-3" style={{ marginTop: '24px' }}>
                {filteredApartments.map((apartment) => {
                  // Check if this apartment is selected in SVG mode
                  const svgId = mapDataIdToSVGId(apartment.id);
                  const isSelectedInSVG = svgId ? selectedApartmentsSVG.includes(svgId) : false;
                  
                  return (
                    <ApartmentCard
                      key={apartment.id}
                      apartment={apartment}
                      isSelected={isSelectedInSVG}
                      onClick={handleApartmentSelect}
                      onHover={handleApartmentCardHover}
                      isMultiSelectMode={isMultiSelectMode}
                      onDeselect={isMultiSelectMode ? (apt) => handleSVGApartmentClick(mapDataIdToSVGId(apt.id) || '') : undefined}
                    />
                  );
                })}
              </div>
            </SidebarContent>
          </ElegantSidebar>
        )}

        {/* Clear markings button for tablets */}
        {markedApartments.size > 0 && (
          <FloatingClearButton onClick={handleClearAllMarkings}>
            <ClearIcon><Trash2 size={16} /></ClearIcon>
            <ClearLabel>Alle Markierungen löschen ({markedApartments.size})</ClearLabel>
          </FloatingClearButton>
        )}

        {/* Desktop-style Buttons for tablets */}
        
        {/* Floating Menu Button - Hidden when table view is open */}
        {!showTableView && (
          <FloatingMenuButton isOpen={isPanelOpen} onClick={togglePanel}>
            {isPanelOpen ? '×' : <Filter size={16} />}
            {!isPanelOpen && <span>Wohnung Suchen</span>}
          </FloatingMenuButton>
        )}

        {/* View Switch Button - Hidden when table view is open */}
        {!showTableView && (
          <ViewSwitchButton 
            onClick={handleViewSwitch}
            style={{
              opacity: (isRealView && !standardImagesLoaded) || (!isRealView && !realImagesLoaded) ? 0.6 : 1,
              cursor: (isRealView && !standardImagesLoaded) || (!isRealView && !realImagesLoaded) ? 'not-allowed' : 'pointer'
            }}
            title={
              (isRealView && !standardImagesLoaded) ? 'Nah-Bilder werden geladen...' :
              (!isRealView && !realImagesLoaded) ? 'Normale-Bilder werden geladen...' :
              'Ansicht wechseln'
            }
          >
            <Eye size={16} />
            {isRealView ? 'Nah Ansicht' : 'Normale Ansicht'}
            {isPreloadingSecondSet && isRealView && (
              <span style={{ marginLeft: '8px', fontSize: '12px' }}>●</span>
            )}
          </ViewSwitchButton>
        )}

        {/* List View Button - Hidden when table view is open */}
        {!showTableView && (
          <ListViewButton 
            isActive={showTableView}
            onClick={handleTableViewToggle}
            title="Alle Wohnungen als Preisliste anzeigen"
          >
            <List size={16} />
            Preisliste
          </ListViewButton>
        )}

        {/* SVG Toggle Button for tablets */}
        {!showTableView && (
          <SVGToggleButton 
            isVisible={textSVGVisible}
            onClick={toggleSVGOverlay}
            title={textSVGVisible ? "Text SVGs ausblenden" : "Text SVGs einblenden"}
          >
            {textSVGVisible ? <Eye size={16} /> : <EyeOff size={16} />}
            {textSVGVisible ? 'Text Ein' : 'Text Aus'}
          </SVGToggleButton>
        )}

        {/* Apartment Modal for tablets - same as desktop */}
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          {selectedApartment && (
            <ApartmentInfo>
              <ApartmentTitle>{selectedApartment.name}</ApartmentTitle>
              {selectedApartment.available ? (
                <ApartmentPrice>{formatPrice(selectedApartment.price)}</ApartmentPrice>
              ) : (
                <ApartmentReservedPrice>Reserviert</ApartmentReservedPrice>
              )}
              
              <ApartmentDetails>
                <DetailItem>
                  <DetailIcon><Building2 size={16} /></DetailIcon>
                  <DetailLabel>Größe</DetailLabel>
                  <DetailValue>{formatSize(selectedApartment.size)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><BedDouble size={16} /></DetailIcon>
                  <DetailLabel>Zimmer</DetailLabel>
                  <DetailValue>{selectedApartment.rooms}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><ArrowUp size={16} /></DetailIcon>
                  <DetailLabel>Etage</DetailLabel>
                  <DetailValue>{getFloorLabel(selectedApartment.floor)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailIcon><Info size={16} /></DetailIcon>
                  <DetailLabel>Bezug ab</DetailLabel>
                  <DetailValue>{selectedApartment.constructionYear || 'N/A'}</DetailValue>
                </DetailItem>
              </ApartmentDetails>
              
              <ApartmentDescription>
                {selectedApartment.description}
              </ApartmentDescription>
              
              <ApartmentFeatures>
                <FeaturesList>
                  {selectedApartment.features.map((feature, index) => (
                    <FeatureTag key={index}>{feature}</FeatureTag>
                  ))}
                </FeaturesList>
              </ApartmentFeatures>

              {selectedApartment.floorPlan && (
                <FloorPlanViewer
                  floorPlan={selectedApartment.floorPlan}
                  floorPlanImages={selectedApartment.floorPlanImages}
                  apartmentName={selectedApartment.name}
                  onFullscreenOpen={handleFullscreenFloorPlanOpen}
                />
              )}

              <ApartmentActions>
                <ContactButton onClick={() => handleContactRequest(selectedApartment)}>
                  Kontakt aufnehmen
                </ContactButton>
                <DownloadPDFButton 
                  onClick={() => downloadFloorPlanPDF(selectedApartment)}
                  disabled={isDownloadingPDF}
                >
                  {isDownloadingPDF ? 'Lädt herunter...' : 'Grundriss Herunterladen'}
                </DownloadPDFButton>
                <ShowApartmentButton onClick={() => handleShowApartment(selectedApartment)}>
                  Wohnung im Gebäude zeigen
                </ShowApartmentButton>
              </ApartmentActions>
            </ApartmentInfo>
          )}
        </Modal>

        {/* Fullscreen Floor Plan Viewer for tablets */}
        {selectedApartment?.floorPlan && (
          <FullscreenFloorPlanViewer
            isOpen={isFullscreenFloorPlanOpen}
            onClose={() => setIsFullscreenFloorPlanOpen(false)}
            floorPlan={selectedApartment.floorPlan}
            floorPlanImages={selectedApartment.floorPlanImages}
            apartmentName={selectedApartment.name}
          />
        )}

        {/* Floating Control Bar for tablets - same as desktop */}
        {!showTableView && (
        <FloatingControlBar isVisible={selectedApartmentsSVG.length > 0 || selectedApartment !== null} isMobile={false}>
          {!isMultiSelectMode ? (
            <FloatingControlButton variant="primary" onClick={enableMultiSelect}>
              ➕ Mehrfachauswahl
            </FloatingControlButton>
          ) : (
            <FloatingControlButton variant="secondary" onClick={disableMultiSelect}>
              ✓ Auswahl beenden
            </FloatingControlButton>
          )}
          <FloatingControlButton variant="danger" onClick={clearSVGSelection}>
            <Trash2 size={16} style={{ display: 'inline', marginRight: '8px' }} />Auswahl löschen
          </FloatingControlButton>
        </FloatingControlBar>
        )}

        {/* Table View overlay when active - same as desktop */}
        {showTableView && (
          <ApartmentTableView
            apartments={allApartments}
            selectedApartment={selectedApartment}
            onApartmentSelect={handleApartmentSelect}
            onClose={handleTableViewToggle}
          />
        )}

      </Container>
    );
  }

  // Revolutionary Desktop Layout
  return (
    <Container>
      {/* Fullscreen 360° Viewer or Table View */}
      <DesktopMainContent>
        <ViewerSection>
          {/* Always render the 360° Viewer */}
          <ViewerContainer isBlurred={showTableView}>
            <Viewer360
            images={currentBuildingImages}
            currentIndex={currentImageIndex}
            onIndexChange={setCurrentImageIndex}
            onRotationChange={handleRotationChange}
            onTouchStateChange={handleTouchStateChange}
            onApartmentClick={handleApartmentClick}
            onApartmentSVGSelect={handleSVGApartmentClick}
            onApartmentSVGClear={clearAllSVGSelection}
            onMobileApartmentDetails={handleMobileApartmentDetails}
            onTabletApartmentListClick={handleTabletApartmentShowClick}
            onApartmentHover={handleApartmentHover}
            externalHoveredApartment={hoveredApartment}
            apartmentColors={apartmentColors}
            selectedApartments={selectedApartmentsSVG}
            apartments={baseFilteredApartments}
            isMultiSelectMode={isMultiSelectMode}
            isRealView={isRealView}
            svgOverlayVisible={svgOverlayVisible}
            textSVGVisible={textSVGVisible}
          />
          </ViewerContainer>
          
          {/* Table View overlay when active */}
          {showTableView && (
            <ApartmentTableView
              apartments={allApartments}
              selectedApartment={selectedApartment}
              onApartmentSelect={handleApartmentSelect}
              onClose={handleTableViewToggle}
            />
          )}
        </ViewerSection>
      </DesktopMainContent>

      {/* Floating Menu Button - Hidden when table view is open */}
      {!showTableView && (
        <FloatingMenuButton isOpen={isPanelOpen} onClick={togglePanel}>
          {isPanelOpen ? '×' : <Filter size={16} />}
          {!isPanelOpen && <span>Wohnung Suchen</span>}
        </FloatingMenuButton>
      )}

      {/* View Switch Button - Hidden when table view is open */}
      {!showTableView && (
        <ViewSwitchButton 
        onClick={handleViewSwitch}
        style={{
          opacity: (isRealView && !standardImagesLoaded) || (!isRealView && !realImagesLoaded) ? 0.6 : 1,
          cursor: (isRealView && !standardImagesLoaded) || (!isRealView && !realImagesLoaded) ? 'not-allowed' : 'pointer'
        }}
        title={
          (isRealView && !standardImagesLoaded) ? 'Nah-Bilder werden geladen...' :
          (!isRealView && !realImagesLoaded) ? 'Normale-Bilder werden geladen...' :
          'Ansicht wechseln'
        }
      >
        <Eye size={16} />
        {isRealView ? 'Nah Ansicht' : 'Normale Ansicht'}
        {isPreloadingSecondSet && isRealView && (
          <span style={{ marginLeft: '8px', fontSize: '12px' }}>●</span>
        )}
        </ViewSwitchButton>
      )}

      {/* List View Button - Hidden when table view is open */}
      {!showTableView && (
        <ListViewButton 
        isActive={showTableView}
        onClick={handleTableViewToggle}
        title="Alle Wohnungen als Preisliste anzeigen"
      >
        <List size={16} />
        Preisliste
        </ListViewButton>
      )}

      {/* SVG Toggle Button - Show on desktop and tablet, hidden when table view is open */}
      {!showTableView && (isDesktop || isTablet) && (
        <SVGToggleButton 
        isVisible={textSVGVisible}
        onClick={toggleSVGOverlay}
        title={textSVGVisible ? "Text SVGs ausblenden" : "Text SVGs einblenden"}
      >
        {textSVGVisible ? <Eye size={16} /> : <EyeOff size={16} />}
        {textSVGVisible ? 'Text Ein' : 'Text Aus'}
        </SVGToggleButton>
      )}

      {/* Elegant Sidebar - Hidden when table view is open */}
      {!showTableView && (
      <ElegantSidebar isOpen={isPanelOpen}>
        <SidebarContent>
          <SidebarHeader>
            <ElegantTitle>Wildbach</ElegantTitle>
            <ElegantSubtitle>
              {apartments.filter(apt => apt.available).length} von {apartments.length} verfügbar
            </ElegantSubtitle>
          </SidebarHeader>

          {/* Filters - only show when no SVG selection */}
          {selectedApartmentsSVG.length === 0 && (
            <PureGlassmorphicFilterPanel
              selectedFloor={selectedFloor}
              selectedRooms={selectedRooms}
              showReserved={showReserved}
              floorOptions={[
                { value: '', label: 'Alle Stockwerke' },
                ...floorLevels.map(floor => ({
                  value: floor.toString(),
                  label: getFloorLabel(floor)
                }))
              ]}
              roomOptions={[
                { value: '', label: 'Alle Zimmeranzahlen' },
                ...roomCounts.map(rooms => ({
                  value: rooms.toString(),
                  label: rooms === 4 ? '4+' : `${rooms}`
                }))
              ]}
              onFloorChange={setSelectedFloor}
              onRoomsChange={setSelectedRooms}
              onShowReservedToggle={setShowReserved}
              onClearFilters={clearFilters}
              resultCount={filteredApartments.length}
              totalCount={apartments.length}
            />
          )}

          {/* SVG Selection Management */}
          {selectedApartmentsSVG.length > 0 && (
            <div>
              <SectionTitle>
                Ausgewählt ({selectedApartmentsSVG.length})
                {isMultiSelectMode && <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', marginLeft: '8px' }}>• Multi aktiv</span>}
              </SectionTitle>
              <div style={{ fontSize: '18px', color: '#ffffff', marginBottom: '24px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
                {selectedApartmentsSVG.map(id => getApartmentNameFromSVGId(id)).join(', ')}
              </div>
            </div>
          )}

          {/* Glassmorphic Smart Apartment Display */}
          <div className="space-y-3" style={{ marginTop: '24px' }}>
            {filteredApartments.map((apartment) => {
              // Check if this apartment is selected in SVG mode
              const svgId = mapDataIdToSVGId(apartment.id);
              const isSelectedInSVG = svgId ? selectedApartmentsSVG.includes(svgId) : false;
              
              return (
                <ApartmentCard
                  key={apartment.id}
                  apartment={apartment}
                  isSelected={isSelectedInSVG}
                  onClick={handleApartmentSelect}
                  onHover={handleApartmentCardHover}
                  isMultiSelectMode={isMultiSelectMode}
                  onDeselect={handleApartmentDeselect}
                />
              );
            })}
          </div>



          <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <SectionTitle>Navigation</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button 
                onClick={() => window.open('https://wildbach-hinwil.ch', '_blank')}
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(248, 250, 252, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}
              >
                ← Zurück zur Projektwebsite
              </button>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(248, 250, 252, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '500',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>
                  <Settings size={16} style={{ display: 'inline', marginRight: '8px' }} />Admin-Bereich
                </button>
              </Link>
            </div>
          </div>
        </SidebarContent>
      </ElegantSidebar>
      )}

      {/* Floating Control Bar - Hidden when table view is open */}
      {!showTableView && (
      <FloatingControlBar isVisible={selectedApartmentsSVG.length > 0 || selectedApartment !== null} isMobile={isMobile}>
        {!isMultiSelectMode ? (
          <FloatingControlButton variant="primary" onClick={enableMultiSelect}>
            ➕ Mehrfachauswahl
          </FloatingControlButton>
        ) : (
          <FloatingControlButton variant="secondary" onClick={disableMultiSelect}>
            ✓ Auswahl beenden
          </FloatingControlButton>
        )}
        <FloatingControlButton variant="danger" onClick={clearSVGSelection}>
                          <Trash2 size={16} style={{ display: 'inline', marginRight: '8px' }} />Auswahl löschen
        </FloatingControlButton>
      </FloatingControlBar>
      )}

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        {selectedApartment && (
          <ApartmentInfo>
            <ApartmentTitle>{selectedApartment.name}</ApartmentTitle>
            {selectedApartment.available ? (
              <ApartmentPrice>{formatPrice(selectedApartment.price)}</ApartmentPrice>
            ) : (
              <ApartmentReservedPrice>Reserviert</ApartmentReservedPrice>
            )}
            
            <ApartmentDetails>
              <DetailItem>
                <DetailIcon><Building2 size={16} /></DetailIcon>
                <DetailLabel>Größe</DetailLabel>
                <DetailValue>{formatSize(selectedApartment.size)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon><BedDouble size={16} /></DetailIcon>
                <DetailLabel>Zimmer</DetailLabel>
                <DetailValue>{selectedApartment.rooms}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon><ArrowUp size={16} /></DetailIcon>
                <DetailLabel>Etage</DetailLabel>
                <DetailValue>{getFloorLabel(selectedApartment.floor)}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailIcon><Info size={16} /></DetailIcon>
                <DetailLabel>Bezug ab</DetailLabel>
                <DetailValue>{selectedApartment.constructionYear || 'N/A'}</DetailValue>
              </DetailItem>
            </ApartmentDetails>
            
            <ApartmentDescription>
              {selectedApartment.description}
            </ApartmentDescription>
            
            <ApartmentFeatures>
              <FeaturesList>
                {selectedApartment.features.map((feature, index) => (
                  <FeatureTag key={index}>{feature}</FeatureTag>
                ))}
              </FeaturesList>
            </ApartmentFeatures>

            {selectedApartment.floorPlan && (
              <FloorPlanViewer
                floorPlan={selectedApartment.floorPlan}
                floorPlanImages={selectedApartment.floorPlanImages}
                apartmentName={selectedApartment.name}
                onFullscreenOpen={handleFullscreenFloorPlanOpen}
              />
            )}

            <ApartmentActions>
              <ContactButton onClick={() => handleContactRequest(selectedApartment)}>
                Kontakt aufnehmen
              </ContactButton>
              <DownloadPDFButton 
                onClick={() => downloadFloorPlanPDF(selectedApartment)}
                disabled={isDownloadingPDF}
              >
                {isDownloadingPDF ? 'Lädt herunter...' : 'Grundriss Herunterladen'}
              </DownloadPDFButton>
              <ShowApartmentButton onClick={() => handleShowApartment(selectedApartment)}>
                Wohnung im Gebäude zeigen
              </ShowApartmentButton>
            </ApartmentActions>
          </ApartmentInfo>
        )}
      </Modal>
      
      <Modal isOpen={isTypeformOpen} onClose={handleCloseTypeform}>
        <TypeformContainer>
          <div data-tf-live="01K0NRBGCX3AZ9EJB04NFS8A39"></div>
        </TypeformContainer>
      </Modal>

      {selectedApartment && selectedApartment.floorPlan && (
        <FullscreenFloorPlanViewer
          floorPlan={selectedApartment.floorPlan}
          floorPlanImages={selectedApartment.floorPlanImages}
          apartmentName={selectedApartment.name}
          isOpen={isFullscreenFloorPlanOpen}
          onClose={handleFullscreenFloorPlanClose}
        />
      )}
    </Container>
  );
};

export default ApartmentFinder; 